import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileText, Trash2 } from "lucide-react";
import { DialogAddMaterial } from "../DialogAddMaterial";

interface LegacyMaterialsSectionProps {
  materialsData: {
    received?: any[];
    used?: any[];
  };
  onAdd: (materialData: any) => void;
  isEditable?: boolean;
}

export function LegacyMaterialsSection({ materialsData, onAdd, isEditable = true }: LegacyMaterialsSectionProps) {
  const [showMaterialModal, setShowMaterialModal] = useState(false);

  const handleDeleteMaterial = (type: 'received' | 'used', index: number) => {
    // Esta função seria implementada pelo componente pai no RDO.tsx original
    console.log('Delete material:', type, index);
  };

  return (
    <Card data-section="materials">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Materiais
          {isEditable && (
            <Dialog open={showMaterialModal} onOpenChange={setShowMaterialModal}>
              <DialogTrigger asChild>
                <Button size="sm" className="ml-2">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </DialogTrigger>
              <DialogAddMaterial 
                open={showMaterialModal} 
                onOpenChange={setShowMaterialModal}
                onAdd={onAdd} 
              />
            </Dialog>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="received" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="received">Recebidos</TabsTrigger>
            <TabsTrigger value="used">Utilizados</TabsTrigger>
          </TabsList>
          <TabsContent value="received" className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Material</TableHead>
                    <TableHead className="text-xs text-center">Quantidade</TableHead>
                    <TableHead className="text-xs text-center">Unidade</TableHead>
                    <TableHead className="text-xs">Fornecedor</TableHead>
                    <TableHead className="text-xs">Documento</TableHead>
                    <TableHead className="text-xs">Observações</TableHead>
                    {isEditable && <TableHead className="text-xs text-center">Ações</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materialsData.received?.map((item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="text-xs">{item.material}</TableCell>
                      <TableCell className="text-xs text-center">{item.quantity}</TableCell>
                      <TableCell className="text-xs text-center">{item.unit}</TableCell>
                      <TableCell className="text-xs">{item.supplier}</TableCell>
                      <TableCell className="text-xs">{item.document}</TableCell>
                      <TableCell className="text-xs">{item.observations}</TableCell>
                      {isEditable && (
                        <TableCell className="text-xs text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMaterial('received', index)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  )) || (
                    <TableRow>
                      <TableCell colSpan={isEditable ? 7 : 6} className="text-center text-muted-foreground">
                        Nenhum material recebido
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="used" className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Material</TableHead>
                    <TableHead className="text-xs text-center">Quantidade</TableHead>
                    <TableHead className="text-xs text-center">Unidade</TableHead>
                    <TableHead className="text-xs">Local</TableHead>
                    <TableHead className="text-xs">Observações</TableHead>
                    {isEditable && <TableHead className="text-xs text-center">Ações</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materialsData.used?.map((item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="text-xs">{item.material}</TableCell>
                      <TableCell className="text-xs text-center">{item.quantity}</TableCell>
                      <TableCell className="text-xs text-center">{item.unit}</TableCell>
                      <TableCell className="text-xs">{item.location}</TableCell>
                      <TableCell className="text-xs">{item.observations}</TableCell>
                      {isEditable && (
                        <TableCell className="text-xs text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMaterial('used', index)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  )) || (
                    <TableRow>
                      <TableCell colSpan={isEditable ? 6 : 5} className="text-center text-muted-foreground">
                        Nenhum material utilizado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}